import { ForwardedRef, forwardRef, ReactElement, ReactNode, useMemo, useRef } from 'react';

import { Box, rem, SystemStyleObject } from '@vimeo/bokeh';
import { ArrowDown, ArrowUp, ReviewQuestion } from '@vimeo/bokeh-icons';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';
import { Optional } from '@vimeo/core/types/generic';

import { QuestionListNotification } from './QuestionListNotification';
import { QuestionsListSorting } from './QuestionsListSorting';
import { useQuestionListNotification } from './useQuestionListNotification';
import { useQuestionsListScrollOnPinnedQuestionChange } from './useQuestionsListScrollOnPinnedQuestionChange';
import { useQuestionsListSorting } from './useQuestionsListSorting';
import { useScrollbarStyles } from '../../../../hooks/styling/useScrollbarStyles';
import { T_NO_QUESTIONS_YET, T_SELECTED, T_UNREAD_QUESTIONS } from '../../../../translations';
import { EQnaTab, IQnAQuestion } from '../../../../types/interaction_tools';
import { createDomName } from '../../../../utils/dom/identifying';
import { EmptyStatePlaceholder } from '../../../panels/empty';
import { createTabListColumnStyle, createTabListScrollStyle } from '../../../panels/styles';

interface IQuestionsListProps extends IBaseDomElementProps {
  tabId: EQnaTab;
  selectedQuestionElement?: Optional<HTMLDivElement>;
  questions: Array<IQnAQuestion>;
  pinnedQuestionId?: Optional<string>;
  itemRenderer: (question: IQnAQuestion) => ReactNode;
  fullWidth?: boolean;
  isWithSorting?: boolean;
  isManagementAccessed?: boolean;
  placeholder?: ReactElement;
  scrollBackground?: string | undefined;
  onSortingChange?: () => void;
}

// todo: Does it need questions list at all? Probably just isEmpty flag.
export const QuestionsList = forwardRef(
  (
    {
      id = createDomName('questions-list'),
      className = createDomName('questions-list'),
      tabId,
      selectedQuestionElement = null,
      questions = [],
      pinnedQuestionId,
      fullWidth,
      isManagementAccessed = false,
      itemRenderer,
      placeholder,
      scrollBackground = 'surface',
    }: IQuestionsListProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const listRef = useRef<Optional<HTMLDivElement>>(null);
    const [listSorting, changeListSorting, sortingFunction] = useQuestionsListSorting(tabId);

    const {
      canShowNewQuestionsNotification,
      canShowScrolledAway,
      scrolledAwayNotificationPosition,
      newQuestionNotificationPosition,
      onScrollInQuestionList,
      onConfirmShowNewQuestions,
      onConfirmScrollToSelectedQuestion,
      onDismissNewQuestionsNotification,
    } = useQuestionListNotification(listRef, questions, listSorting, selectedQuestionElement);

    useQuestionsListScrollOnPinnedQuestionChange(listRef, pinnedQuestionId, isManagementAccessed);

    const { pinnedQuestion, sortedQuestions } = useMemo(() => {
      const sortedQuestions = questions.sort(sortingFunction);

      if (pinnedQuestionId) {
        return {
          pinnedQuestion: sortedQuestions.find((it) => it.id === pinnedQuestionId),
          sortedQuestions: sortedQuestions.filter((it) => it.id !== pinnedQuestionId),
        };
      } else {
        return { pinnedQuestion: null, sortedQuestions };
      }
    }, [pinnedQuestionId, questions, sortingFunction]);

    const scrollbarStyles: SystemStyleObject = useScrollbarStyles({
      scrollbarColor: scrollBackground,
    });

    if (questions.length) {
      return (
        <>
          <QuestionsListSorting
            tabId={tabId}
            sorting={listSorting}
            changeSorting={changeListSorting}
          />

          <Box
            ref={ref}
            id={createDomName(id, 'scroll')}
            className={createDomName(id, 'scroll')}
            sx={{
              ...createTabListScrollStyle({ fullWidth }),
              ...scrollbarStyles,
              scrollPaddingBottom: rem(16),
            }}
            onScroll={onScrollInQuestionList}
          >
            <Box
              ref={listRef}
              id={createDomName(id, 'columns')}
              className={createDomName(className, 'columns')}
              sx={createTabListColumnStyle()}
            >
              {pinnedQuestion ? itemRenderer(pinnedQuestion) : null}
              {sortedQuestions.map((it) => itemRenderer(it))}
            </Box>
          </Box>

          {canShowNewQuestionsNotification ? (
            <QuestionListNotification
              id={createDomName(id, 'new-questions-notification')}
              className={createDomName(className, 'new-questions-notification')}
              placement={newQuestionNotificationPosition}
              content={T_UNREAD_QUESTIONS}
              onDismiss={onDismissNewQuestionsNotification}
              onClick={onConfirmShowNewQuestions}
            />
          ) : null}

          {canShowScrolledAway ? (
            <QuestionListNotification
              id={createDomName(id, 'scrolled-away-notification')}
              className={createDomName(className, 'scrolled-away-notification')}
              placement={scrolledAwayNotificationPosition as string}
              content={T_SELECTED}
              onClick={onConfirmScrollToSelectedQuestion}
              icon={scrolledAwayNotificationPosition === 'top' ? <ArrowUp /> : <ArrowDown />}
            />
          ) : null}
        </>
      );
    } else {
      return (
        placeholder || (
          <EmptyStatePlaceholder
            id={createDomName(id, 'placeholder')}
            className={createDomName(className, 'placeholder')}
            isWithPadding={true}
            icon={<ReviewQuestion boxSize={'lg'} />}
            description={T_NO_QUESTIONS_YET}
            control={null}
          />
        )
      );
    }
  },
);
